'use client'

import * as React from 'react'
import Image from 'next/image'
import { useReducedMotion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { useFeatureIsOn } from 'growthbook'
import { Button } from '@repo/design-system-cf/components/button'
import { runAfterWindowLoadIdle } from 'utils/dom'
import {
  VideoFeatureCard,
  useVideoFeatureCardContext,
} from '@repo/studio-components-video-feature-card/video-feature-card'

import { FEATURE_GROW_3415_HOMEPAGE_GET_STARTED_CTA } from '@web/utils/features'
import { InfiniteCarousel } from '../infinite-carousel'
import { FEATURE_CARDS } from './feature-cards-data'
import type { FeatureCard } from './feature-cards-data'

const STUDIO_BASE_URL = process.env.NEXT_PUBLIC_STUDIO_CF_URL ?? ''
const LFS_VIDEO_BASE = `${process.env.NEXT_PUBLIC_LFS_URL}/studio/homepage/videos`

export function LandscapeSlider() {
  const t = useTranslations('pages.home.landscape_slider')
  const loopedCards = React.useMemo(
    () => [...FEATURE_CARDS, ...FEATURE_CARDS, ...FEATURE_CARDS],
    [],
  )

  return (
    <div data-testid="homepage-hero-loader">
      <InfiniteCarousel
        itemCount={FEATURE_CARDS.length}
        scrollLeftLabel={t('scroll_left')}
        scrollRightLabel={t('scroll_right')}
        arrowTopClassName="top-[104px] lg:top-[126px] -translate-y-1/2"
      >
        {loopedCards.map((card, index) => {
          const setIndex = Math.floor(index / FEATURE_CARDS.length)
          return (
            <FeatureCardItem
              key={`${card.id}-${setIndex}`}
              data-card
              card={card}
              isLCP={index <= 1}
            />
          )
        })}
      </InfiniteCarousel>
    </div>
  )
}

function FeatureCardItem({
  card,
  isLCP,
  ...rest
}: {
  card: FeatureCard
  isLCP: boolean
  'data-card'?: true
}) {
  const t = useTranslations('pages.home.landscape_slider')
  const title = t(`cards.${card.id}.title` as 'cards.image_editor.title')
  const description = t(`cards.${card.id}.description` as 'cards.image_editor.description')
  const posterUrl = `${LFS_VIDEO_BASE}/${card.posterFileName}`
  const videoUrl = `${LFS_VIDEO_BASE}/${card.videoFileName}`
  const isTryForFree = useFeatureIsOn(FEATURE_GROW_3415_HOMEPAGE_GET_STARTED_CTA)

  return (
    <div className="shrink-0 w-[312px] lg:w-[380px] snap-center" {...rest}>
      <VideoFeatureCard.Root>
        <VideoFeatureCard.Link
          href={`${STUDIO_BASE_URL}${card.studioPath}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={title}
        />
        <VideoFeatureCard.Media>
          <LCPAwareVideo videoUrl={videoUrl} posterUrl={posterUrl} isLCP={isLCP} />
          <VideoFeatureCard.Overlay />
          <VideoFeatureCard.HoverButton>
            <Button variant="primary-inverse" size="regular">
              {isTryForFree ? t('try_for_free') : t('get_started')}
            </Button>
          </VideoFeatureCard.HoverButton>
        </VideoFeatureCard.Media>
        <VideoFeatureCard.Title>{title}</VideoFeatureCard.Title>
        <VideoFeatureCard.Description variant="compact">{description}</VideoFeatureCard.Description>
      </VideoFeatureCard.Root>
    </div>
  )
}

/**
 * Handles video playback with LCP-aware poster rendering.
 * Consumes InView/reduced-motion/hover state from VideoFeatureCard.Root context.
 * Keeps Next.js Image with priority for LCP cards, and defers video loading
 * via requestIdleCallbackPonyfill to avoid competing with LCP for bandwidth.
 */
function LCPAwareVideo({
  videoUrl,
  posterUrl,
  isLCP,
}: {
  videoUrl: string
  posterUrl: string
  isLCP: boolean
}) {
  const { isInView, isHovering } = useVideoFeatureCardContext()
  const prefersReducedMotion = useReducedMotion()
  const videoRef = React.useRef<HTMLVideoElement>(null)

  // Defer video loading: start fetching only after window load + idle so the
  // ~400KB MP4s never compete with the LCP poster or critical JS/CSS.
  React.useEffect(() => {
    const video = videoRef.current
    if (!video || !isInView || prefersReducedMotion) {
      return
    }
    return runAfterWindowLoadIdle(() => {
      video.preload = 'auto'
      void video.play().catch(() => {})
    })
  }, [isInView, prefersReducedMotion])

  // Handle reduced-motion: play on hover, pause on leave
  React.useEffect(() => {
    const video = videoRef.current
    if (!video || !prefersReducedMotion) {
      return
    }
    if (isHovering) {
      void video.play().catch(() => {})
    } else {
      video.pause()
      video.currentTime = 0
    }
  }, [prefersReducedMotion, isHovering])

  return (
    <>
      {/* Always render the poster image for LCP cards so <Image> stays the
          LCP element — it's preloaded and paints fast. The video layers on
          top once it starts playing. For non-LCP cards, swap to video to
          avoid keeping both in the DOM. */}
      {isLCP || !isInView ? (
        <Image
          src={posterUrl}
          alt=""
          fill
          sizes="(min-width: 1024px) 380px, 312px"
          className="object-cover pointer-events-none"
          priority={isLCP}
          loading={isLCP ? 'eager' : 'lazy'}
          fetchPriority={isLCP ? 'high' : undefined}
        />
      ) : null}
      {isInView ? (
        <video
          ref={videoRef}
          src={videoUrl}
          poster={isLCP ? undefined : posterUrl}
          preload="none"
          loop
          muted
          playsInline
          className="absolute inset-0 size-full object-cover pointer-events-none"
          onError={e => e.stopPropagation()}
        />
      ) : null}
    </>
  )
}
